/*
 * ---------------------------------------------------------------------------------
 * Copyright:
 *      NewtonGreen Technologies Pty. Ltd.
 *      Level 4, 175 Scott St.
 *      Newcastle, NSW, 2300
 *      Australia
 *
 *      E-mail: support@newtongreen.com
 *      Tel: (02) 4925 5288
 *      Fax: (02) 4925 3068
 *
 *      All Rights Reserved.
 * ---------------------------------------------------------------------------------
 */

/*
 * --------------------------------------------------------------------------------
 * This file contains the header component
 * --------------------------------------------------------------------------------
 */

/*
 * ---------------------------------------------------------------------------------
 * Imports - External
 * ---------------------------------------------------------------------------------
 */

/**
 * Required to make use of JSX functionality
 */
import * as React from 'react';


import { makeStyles, Theme } from '@material-ui/core/styles';



/*
 * ---------------------------------------------------------------------------------
 * Imports - Internal
 * ---------------------------------------------------------------------------------
 */

import * as Dtos from '../../api/Neon.Dtos';






import OpmsField from '../../components/OpmsForm/OpmsField';

import OpmsInput from '../../components/OpmsForm/OpmsInput';

import TextField from '../../components/OpmsForm/Fields/TextField';

import Select from '../../components/OpmsForm/Fields/Select';

import ToggleButtonGroup from '../../components/OpmsForm/Fields/ToggleButtonGroup';

import Typography from '@material-ui/core/Typography';
import PatientBreadcrumbs from '../../components/Breadcrumbs/PatientBreadcrumbs';
import CtcaeSelector from '../../components/OpmsForm/Fields/CtcaeSelector';
import MaskedField, { UK_DATE } from '../../components/OpmsForm/Fields/MaskedField';
import OpmsFormGrid, { IOpmsFormGridCell } from '../../components/OpmsForm/Grids/Form/OpmsFormGrid';
import OpmsConditional, { IOpmsContionParams } from '../../components/OpmsForm/OpmsConditional';
import OpmsFieldGroup from '../../components/OpmsForm/OpmsFieldGroup';
import TrialForm from '../../components/OpmsForm/TrialForm';
import { GetFieldLookupItem } from '../../context/LookupsContext';
import { PatientContext } from '@ngt/online-patient-management-ui';
import Checkbox from '../../components/OpmsForm/Fields/Checkbox';
import { Box, InputAdornment, Paper } from '@material-ui/core';
import NumericField, { FIVE_DIGIT_INTEGER_TWO_DECIMAL_PLACES_POSITIVE } from '../../components/OpmsForm/Fields/NumericField';
import DatePicker from '../../components/OpmsForm/Fields/DatePicker';
import OpmsStaticGrid, { IOpmsStaticGridHeader } from '../../components/OpmsForm/Grids/Static/OpmsStaticGrid';
import OpmsStaticRow, { IOpmsStaticRow } from '../../components/OpmsForm/Grids/Static/OpmsStaticRow';
import OpmsFieldLabel from '../../components/OpmsForm/OpmsFieldLabel';
import PageLayout from 'src/components/Layout/PageLayout';

/*
 * ---------------------------------------------------------------------------------
 * Interfaces
 * ---------------------------------------------------------------------------------
 */

interface IMedicalHistoryAndConcomitantMedicationsProps {
}

/*
 * ---------------------------------------------------------------------------------
 * Styles
 * ---------------------------------------------------------------------------------
 */

const useStyles = makeStyles<Theme>(theme => ({
    container: {
        padding: theme.spacing(0)
    },
    heading: {
        padding: theme.spacing(3)
    },
    subHeading: {
        padding: theme.spacing(3)
    },
    instruction: {
        padding: theme.spacing(2, 3, 1, 3)
    }
}));

/*
 * ---------------------------------------------------------------------------------
 * Functions
 * ---------------------------------------------------------------------------------
 */
const haRelevantDataToBeAdded = ({ lookups, formState }: IOpmsContionParams<Dtos.MedicalHistoryAndConcomitantMedications>) => GetFieldLookupItem(lookups, 'RelevantDataToBeAddedId', formState.values.relevantDataToBeAddedId!)?.type === Dtos.LookupYesNoType.Yes;

const hasMedicalCondition = ({ lookups, formState }: IOpmsContionParams<Dtos.MedicalHistoryAndConcomitantMedications>) => !formState.values.medicalConditionNone;
const hasMedicalMedicalHistory = ({ lookups, formState }: IOpmsContionParams<Dtos.MedicalHistoryAndConcomitantMedications>) => !formState.values.otherMedicalHistoryNone;
const hasConcomitantMedication = ({ lookups, formState }: IOpmsContionParams<Dtos.MedicalHistoryAndConcomitantMedications>) => !formState.values.concomitantMedicalNone;
const hasPreviousPregnancies = ({ lookups, formState }: IOpmsContionParams<Dtos.MedicalHistoryAndConcomitantMedications>) => (formState.values.numberOfPreviousPregnancies ?? 0) > 0;
const hasLiveBirths = ({ lookups, formState }: IOpmsContionParams<Dtos.MedicalHistoryAndConcomitantMedications>) => (formState.values.numberOfLiveBirths ?? 0) > 0;
const didBreastFeed = ({ lookups, formState }: IOpmsContionParams<Dtos.MedicalHistoryAndConcomitantMedications>) => formState.values.didParticipantBreastFeed === Dtos.LookupYesNoType.Yes;
const isFemale = ({ lookups, formState }: IOpmsContionParams<Dtos.MedicalHistoryAndConcomitantMedications>) => (formState.values.sex ?? 0) === Dtos.LookupGenderType.Female;
const participantHaveIud = ({ lookups, formState }: IOpmsContionParams<Dtos.MedicalHistoryAndConcomitantMedications>) => formState.values.participantHasIud === Dtos.LookupYesNoType.Yes;
const hormonalBloodsRequired = ({ lookups, formState }: IOpmsContionParams<Dtos.MedicalHistoryAndConcomitantMedications>) => formState.values.menopausalStatus === Dtos.LookupMenopausalStatusType.PostMenopausal && (formState.values.age ?? 0) < 60 && formState.values.bilateralOophorectomy !== true;
const pregnancyTestRequired = ({ lookups, formState }: IOpmsContionParams<Dtos.MedicalHistoryAndConcomitantMedications>) => formState.values.menopausalStatus === Dtos.LookupMenopausalStatusType.PreOrPeriMenopausal && formState.values.bilateralSalpingectomy !== true && formState.values.hysterectomy !== true;

const doesNotHaveLhLlnNotSpecified = ({ formState }: IOpmsContionParams<Dtos.MedicalHistoryAndConcomitantMedications>) => !formState.values.postmenopausalLevelsLhLlnNotSpecified;
const doesNotHaveLhUlnNotSpecified = ({ formState }: IOpmsContionParams<Dtos.MedicalHistoryAndConcomitantMedications>) => !formState.values.postmenopausalLevelsLhUlnNotSpecified;
const doesNotHaveFshLlnNotSpecified = ({ formState }: IOpmsContionParams<Dtos.MedicalHistoryAndConcomitantMedications>) => !formState.values.postmenopausalLevelsFshLlnNotSpecified;
const doesNotHaveFshUlnNotSpecified = ({ formState }: IOpmsContionParams<Dtos.MedicalHistoryAndConcomitantMedications>) => !formState.values.postmenopausalLevelsFshUlnNotSpecified;

const lastMenstrualLessThan12Months = ({ lookups, formState }: IOpmsContionParams<Dtos.MedicalHistoryAndConcomitantMedications>) => formState.values.lastMenstrualPeriodId === Dtos.LookupLastMenstrualPeriodType.LessThan12Months;

/*
 * ---------------------------------------------------------------------------------
 * Constants
 * ---------------------------------------------------------------------------------
 */
const levelsHeader: IOpmsStaticGridHeader[] = [
    {
        columnName: 'test',
        content: 'Test',
        width: '14%',
        minWidth: 100
    },
    {
        columnName: 'value',
        content: '',
        width: '22%',
        minWidth: 150
    },
    {
        columnName: 'lln',
        content: 'Post Menopausal LLN',
        width: '22%',
        minWidth: 150
    },
    {
        columnName: 'llnNotSpecified',
        content: '',
        width: '10%',
        minWidth: 100
    },
    {
        columnName: 'uln',
        content: 'Post Menopausal ULN',
        width: '22%',
        minWidth: 150
    },
    {
        columnName: 'ulnNotSpecified',
        content: '',
        width: '10%',
        minWidth: 100
    }
];

const lhRow: Array<IOpmsStaticRow<Dtos.MedicalHistoryAndConcomitantMedications>> = [
    {
        columnName: 'test',
        content: (
            <OpmsFieldLabel />
        ),
        fieldName: 'PostmenopausalLevelsLh'
    },
    {
        columnName: 'value',
        content: (
            <OpmsInput
                component={NumericField}
                InputProps={{
                    endAdornment: (
                        <InputAdornment position="end">
                            U/L&nbsp;
                        </InputAdornment>
                    )
                }}
                numberFormat={FIVE_DIGIT_INTEGER_TWO_DECIMAL_PLACES_POSITIVE}
            />
        ),
        fieldName: 'PostmenopausalLevelsLh'
    },
    {
        columnName: 'lln',
        content: (
            <OpmsInput
                component={NumericField}
                InputProps={{
                    endAdornment: (
                        <InputAdornment position="end">
                            U/L&nbsp;
                        </InputAdornment>
                    )
                }}
                numberFormat={FIVE_DIGIT_INTEGER_TWO_DECIMAL_PLACES_POSITIVE}
            />
        ),
        condition: doesNotHaveLhLlnNotSpecified,
        subscription: { values: true },
        fieldName: 'PostmenopausalLevelsLhLln'
    },
    {
        columnName: 'llnNotSpecified',
        content: (
            <OpmsInput
                component={Checkbox}
                label={'NA'}
            />
        ),
        fieldName: 'PostmenopausalLevelsLhLlnNotSpecified'
    },
    {
        columnName: 'uln',
        content: (
            <OpmsInput
                component={NumericField}
                InputProps={{
                    endAdornment: (
                        <InputAdornment position="end">
                            U/L&nbsp;
                        </InputAdornment>
                    )
                }}
                numberFormat={FIVE_DIGIT_INTEGER_TWO_DECIMAL_PLACES_POSITIVE}
            />
        ),
        condition: doesNotHaveLhUlnNotSpecified,
        subscription: { values: true },
        fieldName: 'PostmenopausalLevelsLhUln'
    },
    {
        columnName: 'ulnNotSpecified',
        content: (
            <OpmsInput
                component={Checkbox}
                label={'NA'}
            />
        ),
        fieldName: 'PostmenopausalLevelsLhUlnNotSpecified'
    }
];

const fshRow: Array<IOpmsStaticRow<Dtos.MedicalHistoryAndConcomitantMedications>> = [
    {
        columnName: 'test',
        content: (
            <OpmsFieldLabel />
        ),
        fieldName: 'PostmenopausalLevelsFsh'
    },
    {
        columnName: 'value',
        content: (
            <OpmsInput
                component={NumericField}
                InputProps={{
                    endAdornment: (
                        <InputAdornment position="end">
                            U/L&nbsp;
                        </InputAdornment>
                    )
                }}
                numberFormat={FIVE_DIGIT_INTEGER_TWO_DECIMAL_PLACES_POSITIVE}
            />
        ),
        fieldName: 'PostmenopausalLevelsFsh'
    },
    {
        columnName: 'lln',
        content: (
            <OpmsInput
                component={NumericField}
                InputProps={{
                    endAdornment: (
                        <InputAdornment position="end">
                            U/L&nbsp;
                        </InputAdornment>
                    )
                }}
                numberFormat={FIVE_DIGIT_INTEGER_TWO_DECIMAL_PLACES_POSITIVE}
            />
        ),
        condition: doesNotHaveFshLlnNotSpecified,
        subscription: { values: true },
        fieldName: 'PostmenopausalLevelsFshLln'
    },
    {
        columnName: 'llnNotSpecified',
        content: (
            <OpmsInput
                component={Checkbox}
                label={'NA'}
            />
        ),
        fieldName: 'PostmenopausalLevelsFshLlnNotSpecified'
    },
    {
        columnName: 'uln',
        content: (
            <OpmsInput
                component={NumericField}
                InputProps={{
                    endAdornment: (
                        <InputAdornment position="end">
                            U/L&nbsp;
                        </InputAdornment>
                    )
                }}
                numberFormat={FIVE_DIGIT_INTEGER_TWO_DECIMAL_PLACES_POSITIVE}
            />
        ),
        condition: doesNotHaveFshUlnNotSpecified,
        subscription: { values: true },
        fieldName: 'PostmenopausalLevelsFshUln'
    },
    {
        columnName: 'ulnNotSpecified',
        content: (
            <OpmsInput
                component={Checkbox}
                label={'NA'}
            />
        ),
        fieldName: 'PostmenopausalLevelsFshUlnNotSpecified'
    }
];

/*
 * ---------------------------------------------------------------------------------
 * Components
 * ---------------------------------------------------------------------------------
 */

const MedicalHistoryAndConcomitantMedications: React.FunctionComponent<IMedicalHistoryAndConcomitantMedicationsProps> = () => {
    const classes = useStyles();

    const [patient] = React.useContext(PatientContext);

    const patientCohortC = (patient as Dtos.Patient)?.treatmentId !== 1 && (patient as Dtos.Patient)?.treatmentId !== 2;

    const medicalHistoryColumns: Array<IOpmsFormGridCell<Dtos.OtherMedicalCondition>> = React.useMemo(() => {
        return [
            {
                name: 'CtcaeTermId',
                content: (
                    <OpmsInput
                        component={CtcaeSelector}
                        gradeFieldName="CtcaeGradeId"
                        specificationFieldName="CtcaeSpecification"
                        display="SelectorWithName"
                    />
                ),
                minWidth: 440
            },
            {
                name: 'CurrentTimeRandomisationId',
                content: (
                    <OpmsInput
                        component={Select}
                    />
                ),
                minWidth: 278,
                header: <Typography>
                    <strong>{patientCohortC ? 'Current at time of registration' : 'Current at time of randomisation'}</strong>
                </Typography>
            },
            {
                name: 'DiagnosisDate',
                content: (
                    <OpmsInput
                        component={MaskedField}
                        maskedFormat={UK_DATE}
                    />
                ),
                minWidth: 180
            },
            {
                name: 'TreatmentOngoingId',
                content: (
                    <OpmsInput
                        component={Select}
                    />
                ),
                minWidth: 180
            }
        ];
    }, [patientCohortC]);

    const otherMedicalHistoryColumns: Array<IOpmsFormGridCell<Dtos.OtherMedicalCondition>> = React.useMemo(() => {
        return [
            {
                name: 'MedicalCondition',
                content: (
                    <OpmsInput
                        component={TextField}
                    />
                ),
                minWidth: 440
            },
            {
                name: 'CurrentTimeRandomisationId',
                content: (
                    <OpmsInput
                        component={Select}
                    />
                ),
                minWidth: 278,
                header: <Typography>
                    <strong>{ patientCohortC ? 'Current at time of registration' : 'Current at time of randomisation' }</strong>
                </Typography>
            },
            {
                name: 'DiagnosisDate',
                content: (
                    <OpmsInput
                        component={MaskedField}
                        maskedFormat={UK_DATE}
                    />
                ),
                minWidth: 180
            },
            {
                name: 'TreatmentOngoingId',
                content: (
                    <OpmsInput
                        component={Select}
                    />
                ),
                minWidth: 180
            }
        ]
    }, [patientCohortC])

    return (
        <PageLayout
            breadcrumbs={
                <PatientBreadcrumbs />
            }
            heading="Medical History and Concomitant Medication"
        >
            <Paper
                className={classes.container}
            >
                <TrialForm
                    formClass={Dtos.MedicalHistoryAndConcomitantMedications}
                >
                    {
                        patientCohortC && <>
                            {
                                <OpmsConditional
                                    formClass={Dtos.MedicalHistoryAndConcomitantMedications}
                                    mode="Enable"
                                    condition={isFemale}
                                    subscription={{ values: true }}
                                >
                                    <OpmsFieldGroup>
                                        <Typography
                                            variant="h5"
                                            color="primary"
                                            className={classes.heading}
                                        >
                                            Reproductive History
                                        </Typography>
                                        <OpmsField
                                            name="AgeAtFirstMenstrualPeriod"
                                        >
                                            <OpmsInput
                                                component={NumericField}
                                                numberFormat={{
                                                    decimalScale: 0
                                                }}
                                            />
                                        </OpmsField>
                                        <OpmsField
                                            name="AgeAtLastMenstrualPeriod"
                                        >
                                            <OpmsInput
                                                component={NumericField}
                                                numberFormat={{
                                                    decimalScale: 0
                                                }}
                                            />
                                        </OpmsField>
                                        <OpmsField
                                            name="LastMenstrualPeriodId"
                                        >
                                            <OpmsInput
                                                component={Select}
                                            />
                                        </OpmsField>
                                        <OpmsConditional
                                            formClass={Dtos.MedicalHistoryAndConcomitantMedications}
                                            mode="Show"
                                            condition={lastMenstrualLessThan12Months}
                                            subscription={{ values: true }}
                                        >
                                            <OpmsField
                                                name="LastMenstrualPeriodDate"
                                            >
                                                <OpmsInput
                                                    component={DatePicker}
                                                />
                                            </OpmsField>
                                        </OpmsConditional>
                                        <OpmsField
                                            name="BilateralOophorectomy"
                                            label={<Typography>Has the participant had any of the following surgical procedures</Typography>}
                                        >
                                            <OpmsInput
                                                component={Checkbox}
                                                label="Bilateral oophorectomy"
                                            />
                                        </OpmsField>
                                        <OpmsField
                                            name="BilateralSalpingectomy"
                                            label=""
                                        >
                                            <OpmsInput
                                                component={Checkbox}
                                                label="Bilateral salpingectomy/tubal ligation"
                                            />
                                        </OpmsField>
                                        <OpmsField
                                            name="Hysterectomy"
                                            label=""
                                        >
                                            <OpmsInput
                                                component={Checkbox}
                                                label="Hysterectomy"
                                            />
                                        </OpmsField>
                                        <OpmsField
                                            name="EndometrialAblation"
                                            label=""
                                        >
                                            <OpmsInput
                                                component={Checkbox}
                                                label="Endometrial ablation"
                                            />
                                        </OpmsField>
                                        <OpmsField
                                            name="ParticipantHasIud"
                                        >
                                            <OpmsInput
                                                component={Select}
                                            />
                                        </OpmsField>
                                        <OpmsConditional
                                            formClass={Dtos.MedicalHistoryAndConcomitantMedications}
                                            mode="Enable"
                                            condition={participantHaveIud}
                                            subscription={{ values: true }}
                                        >
                                            <OpmsField
                                                name="IudHormonalNonHormonal"
                                            >
                                                <OpmsInput
                                                    component={Select}
                                                />
                                            </OpmsField>
                                        </OpmsConditional>
                                        <OpmsField
                                            name="MenopausalStatus"
                                        >
                                            <OpmsInput
                                                component={Select}
                                            />
                                        </OpmsField>
                                        <OpmsConditional
                                            formClass={Dtos.MedicalHistoryAndConcomitantMedications}
                                            mode="Show"
                                            condition={hormonalBloodsRequired}
                                            subscription={{ values: true }}
                                        >
                                            <OpmsField name="PostmenopausalLevelsDate">
                                                <OpmsInput
                                                    component={DatePicker}
                                                />
                                            </OpmsField>
                                            <OpmsStaticGrid
                                                headers={levelsHeader}
                                            >
                                                <OpmsStaticRow
                                                    cells={lhRow}
                                                />
                                                <OpmsStaticRow
                                                    cells={fshRow}
                                                />
                                            </OpmsStaticGrid>
                                        </OpmsConditional>
                                        <OpmsConditional
                                            formClass={Dtos.MedicalHistoryAndConcomitantMedications}
                                            mode="Show"
                                            condition={pregnancyTestRequired}
                                            subscription={{ values: true }}
                                        >
                                            <OpmsField name="PreOrPerimenopausalNegativePregnancyTestId">
                                                <OpmsInput
                                                    component={ToggleButtonGroup}
                                                />
                                            </OpmsField>
                                            <OpmsField name="PreOrPerimenopausalNegativePregnancyTestDate">
                                                <OpmsInput
                                                    component={DatePicker}
                                                />
                                            </OpmsField>
                                        </OpmsConditional>
                                        <OpmsField
                                            name="NumberOfPreviousPregnancies"
                                        >
                                            <OpmsInput
                                                component={NumericField}
                                                numberFormat={{
                                                    decimalScale: 0
                                                }}
                                            />
                                        </OpmsField>
                                        <OpmsConditional
                                            formClass={Dtos.MedicalHistoryAndConcomitantMedications}
                                            mode="Enable"
                                            condition={hasPreviousPregnancies}
                                            subscription={{ values: true }}
                                        >
                                            <OpmsField
                                                name="AgeAtFirstPregnancy"
                                            >
                                                <OpmsInput
                                                    component={NumericField}
                                                    numberFormat={{
                                                        decimalScale: 0
                                                    }}
                                                />
                                            </OpmsField>
                                            <OpmsField
                                                name="NumberOfLiveBirths"
                                            >
                                                <OpmsInput
                                                    component={NumericField}
                                                    numberFormat={{
                                                        decimalScale: 0
                                                    }}
                                                />
                                            </OpmsField>
                                        </OpmsConditional>
                                        <OpmsConditional
                                            formClass={Dtos.MedicalHistoryAndConcomitantMedications}
                                            mode="Enable"
                                            condition={hasLiveBirths}
                                            subscription={{ values: true }}
                                        >
                                            <OpmsField
                                                name="DidParticipantBreastFeed"
                                            >
                                                <OpmsInput
                                                    component={ToggleButtonGroup}
                                                />
                                            </OpmsField>
                                        </OpmsConditional>
                                        <OpmsConditional
                                            formClass={Dtos.MedicalHistoryAndConcomitantMedications}
                                            mode="Enable"
                                            condition={didBreastFeed}
                                            subscription={{ values: true }}
                                        >
                                            <OpmsField
                                                name="CumulativeMonthsBreastFeedingDuration"
                                            >
                                                <OpmsInput
                                                    component={NumericField}
                                                    numberFormat={{
                                                        decimalScale: 0
                                                    }}
                                                />
                                            </OpmsField>
                                        </OpmsConditional>
                                        <OpmsConditional
                                            formClass={Dtos.MedicalHistoryAndConcomitantMedications}
                                            mode="Enable"
                                            condition={hasLiveBirths}
                                            subscription={{ values: true }}
                                        >
                                            <OpmsField
                                                name="AgeOfFirstChild"
                                            >
                                                <OpmsInput
                                                    component={NumericField}
                                                    numberFormat={{
                                                        decimalScale: 1
                                                    }}
                                                />
                                            </OpmsField>
                                            <OpmsField
                                                name="AgeOfLastChild"
                                            >
                                                <OpmsInput
                                                    component={NumericField}
                                                    numberFormat={{
                                                        decimalScale: 1
                                                    }}
                                                />
                                            </OpmsField>
                                        </OpmsConditional>
                                    </OpmsFieldGroup>
                                </OpmsConditional>
                                
                            }
                            <OpmsFieldGroup>
                                <OpmsField
                                    name="MedicalConditionNone"
                                    label={
                                        <Box style={{ marginTop: '2' }}>
                                            <Typography
                                                variant="h5"
                                                color="primary"
                                                style={{ marginTop: '0.5rem' }}
                                            >
                                                Ongoing NCI CTCAE Medical Condition
                                            </Typography>
                                        </Box>
                                    }
                                    xl={8}
                                    lg={8}
                                    md={8}
                                    sm={12}
                                    xs={12}
                                    paddingTop={1}
                                >
                                    <OpmsInput
                                        component={Checkbox}
                                        label="None"
                                    />
                                </OpmsField>
                                <OpmsConditional
                                    formClass={Dtos.MedicalHistoryAndConcomitantMedications}
                                    mode="Enable"
                                    condition={hasMedicalCondition}
                                    subscription={{ values: true }}
                                >
                                    <OpmsFormGrid
                                        name="MedicalConditions"
                                        columns={medicalHistoryColumns}
                                    />
                                </OpmsConditional>
                            </OpmsFieldGroup>
                            <OpmsFieldGroup>
                                <OpmsField
                                    name="OtherMedicalHistoryNone"
                                    label={
                                        <Box style={{ marginTop: '2' }}>
                                            <Typography
                                                variant="h5"
                                                color="primary"
                                                style={{ marginTop: '0.5rem' }}
                                            >
                                                Other Relevant Medical History
                                            </Typography>
                                        </Box>
                                    }
                                    xl={8}
                                    lg={8}
                                    md={8}
                                    sm={12}
                                    xs={12}
                                    paddingTop={1}
                                >
                                    <OpmsInput
                                        component={Checkbox}
                                        label="None"
                                    />
                                </OpmsField>
                                <OpmsConditional
                                    formClass={Dtos.MedicalHistoryAndConcomitantMedications}
                                    mode="Enable"
                                    condition={hasMedicalMedicalHistory}
                                    subscription={{ values: true }}
                                >
                                    <OpmsFormGrid
                                        name="OtherMedicalConditions"
                                        columns={otherMedicalHistoryColumns}
                                    />
                                </OpmsConditional>
                            </OpmsFieldGroup>
                            <OpmsFieldGroup>
                                <OpmsField
                                    name="ConcomitantMedicalNone"
                                    label={
                                        <Box style={{ marginTop: '2' }}>
                                            <Typography
                                                variant="h5"
                                                color="primary"
                                                style={{ marginTop: '0.5rem' }}
                                            >
                                                Concomitant Medication
                                            </Typography>
                                        </Box>
                                    }
                                    xl={8}
                                    lg={8}
                                    md={8}
                                    sm={12}
                                    xs={12}
                                    paddingTop={1}
                                >
                                    <OpmsInput
                                        component={Checkbox}
                                        label="None"
                                    />
                                </OpmsField>
                                <Typography
                                    className={classes.instruction}
                                >
                                    Report all medications ongoing at the time of study registration including all medications for immune endocrinopathies (e.g. thyroid, pituitary, adrenal, diabetes). Note: current use of oral, injectable or implant hormonal contraceptives or medicated IUD are prohibited
                                </Typography>
                                <OpmsConditional
                                    formClass={Dtos.MedicalHistoryAndConcomitantMedications}
                                    mode="Enable"
                                    condition={hasConcomitantMedication}
                                    subscription={{ values: true }}
                                >
                                    <OpmsFormGrid
                                        name="ConcomitantMedications"
                                        columns={[
                                            {
                                                name: 'MedicationName',
                                                content: (
                                                    <OpmsInput
                                                        component={TextField}
                                                    />
                                                )
                                            },
                                            {
                                                name: 'IndicationId',
                                                content: (
                                                    <OpmsInput
                                                        component={Select}
                                                    />
                                                )
                                            },
                                            {
                                                name: 'StartDate',
                                                content: (
                                                    <OpmsInput
                                                        component={MaskedField}
                                                        maskedFormat={UK_DATE}
                                                    />
                                                )
                                            }
                                        ]}
                                    />
                                </OpmsConditional>
                            </OpmsFieldGroup>
                        </>
                    }

                    {
                        !patientCohortC && <>
                            <OpmsField name="RelevantDataToBeAddedId">
                                <OpmsInput
                                    component={ToggleButtonGroup}
                                />
                            </OpmsField>
                            <OpmsFieldGroup>
                                <Typography
                                    variant="h2"
                                    color="primary"
                                    className={classes.subHeading}
                                >
                                    Relevant Medical History
                                </Typography>
                                <Typography
                                    variant="h5"
                                    color="primary"
                                    className={classes.instruction}
                                >
                                    <strong>Instructions</strong>: Please report ongoing conditions using the NCI CTCAE v5.0 table where possible. For medical History that cannot be classified by NCI CTCAE (e.g. relevant surgical history) please use the Other relevant Medical  History table.
                                </Typography>
                        
                                <OpmsConditional
                                    formClass={Dtos.MedicalHistoryAndConcomitantMedications}
                                    mode="Enable"
                                    condition={haRelevantDataToBeAdded}
                                    subscription={{ values: true }}
                                >
                                    <Typography
                                        variant="h5"
                                        color="primary"
                                        className={classes.subHeading}
                                    >
                                        Ongoing NCI CTCAE Medical Condition
                                    </Typography>
                            
                                    <OpmsFormGrid
                                        name="MedicalConditions"
                                        columns={medicalHistoryColumns}
                                    />

                                    <Typography
                                        variant="h5"
                                        color="primary"
                                        className={classes.subHeading}
                                    >
                                        Other relevant Medical History
                                    </Typography>

                                    <OpmsFormGrid
                                        name="OtherMedicalConditions"
                                        columns={otherMedicalHistoryColumns}
                                    />
                                </OpmsConditional>
                            </OpmsFieldGroup>
                            <OpmsFieldGroup>
                                <Typography
                                    variant="h2"
                                    color="primary"
                                    className={classes.subHeading}
                                >
                                    Concomitant Medication
                                </Typography>

                                <OpmsConditional
                                    formClass={Dtos.MedicalHistoryAndConcomitantMedications}
                                    mode="Enable"
                                    condition={haRelevantDataToBeAdded}
                                    subscription={{ values: true }}
                                >
                                    <OpmsFormGrid
                                        name="ConcomitantMedications"
                                        columns={[
                                            {
                                                name: 'MedicationName',
                                                content: (
                                                    <OpmsInput
                                                        component={TextField}
                                                    />
                                                )
                                            },
                                            {
                                                name: 'IndicationId',
                                                content: (
                                                    <OpmsInput
                                                        component={Select}
                                                    />
                                                )
                                            },
                                            {
                                                name: 'StartDate',
                                                content: (
                                                    <OpmsInput
                                                        component={MaskedField}
                                                        maskedFormat={UK_DATE}
                                                    />
                                                )
                                            }
                                        ]}
                                    />
                                </OpmsConditional>
                            </OpmsFieldGroup>
                        </>
                        
                        

                    }
                </TrialForm>
            </Paper>
        </PageLayout>
    );
};

/*
 * ---------------------------------------------------------------------------------
 * Default Export
 * ---------------------------------------------------------------------------------
 */
export default MedicalHistoryAndConcomitantMedications;